/**
 * Avance de obra en el enlace para el cliente: de la foto pública (`AvancePublico`: solo fracciones, fechas y piezas
 * del propio archivo) a lo que necesita el visor. Puro, sin DOM salvo la huella (`crypto.subtle`, inyectable).
 *
 * Los periodos no traen número de estimación: su identidad es su posición, y aquí se les da `numero = índice + 1`
 * para reutilizar `nudosDeAvance` del diálogo de staff.
 */
import {
  nudosDeAvance,
  type AvanceObraPeriodo,
  type AvancePublico,
  type ConceptoAvancePublico,
  type ConceptoParaPintar,
  type PiezaDeConcepto,
} from '@supernova/shared';
import {
  dentroDeCaja,
  huellaDeNombre,
  ordenDeConstruccion,
  piezasDwgDeHandles,
  type CajaDePieza,
} from './avanceObra4d.js';

/** Lo que el dibujo ya leído deja saber (un subconjunto de `CensoDelPlano`). */
export interface CensoParaAvance {
  handlesDeCapa: (capa: string, vista?: string | null) => string[];
  handlesDeBloque: (nombre: string) => string[];
  capas: () => string[];
  bloques: () => string[];
  centro: (h: string) => { x: number; y: number } | undefined;
  tamano: (h: string) => number | undefined;
}

/** Los periodos para la curva S: el «importe» es el % de la obra (0..1), para que la curva se dibuje en porcentaje. */
export function periodosParaCurva(a: AvancePublico): AvanceObraPeriodo[] {
  let previo = 0;
  return a.periodos.map((p, i) => {
    // Un periodo sin % medible arrastra el último valor conocido (no baja la curva ni da un periodo negativo).
    const acum = p.pctObra ?? previo;
    const o: AvanceObraPeriodo = {
      numero: i + 1,
      fechaInicial: p.desde,
      fechaFinal: p.hasta,
      tipo: 'ordinaria',
      tipoPeriodo: null,
      importePeriodo: acum - previo,
      importeAcumulado: acum,
    };
    previo = acum;
    return o;
  });
}

/** ¿La obra se puede medir en %? (si ningún periodo trae pctObra, no se muestra el porcentaje ni la curva) */
export const hayPorcentajeDeObra = (a: AvancePublico): boolean => a.periodos.some((p) => p.pctObra !== null);

export function nudosDeConcepto(a: AvancePublico, c: ConceptoAvancePublico, periodos = periodosParaCurva(a)) {
  return nudosDeAvance(periodos, new Map(c.avance.map(([i, p]) => [i + 1, p] as const)));
}

/** IFC: los conceptos con piezas, con las piezas en orden de construcción (necesita las cajas del visor). */
export function pintablesIfc(a: AvancePublico, cajas: ReadonlyMap<number, CajaDePieza>): ConceptoParaPintar[] {
  const periodos = periodosParaCurva(a);
  const out: ConceptoParaPintar[] = [];
  for (const c of a.conceptos) {
    if (!c.piezasIfc || c.piezasIfc.length === 0) continue;
    const piezas: PiezaDeConcepto[] = c.piezasIfc.map(([id, peso]) => ({ id: String(id), peso }));
    out.push({ nudos: nudosDeConcepto(a, c, periodos), piezas: ordenDeConstruccion(piezas, cajas) });
  }
  return out;
}

/**
 * DWG/DXF: huella → nombre real de capa y de bloque, armado con lo que el dibujo trae. Un bloque del inventario puede venir sin el prefijo
 * de xref («…$0$puerta»), así que también se guarda la huella del nombre corto (el visor lo busca por sufijo).
 */
export async function huellasDelDibujo(
  censo: Pick<CensoParaAvance, 'capas' | 'bloques'>,
  huella: (n: string) => Promise<string> = huellaDeNombre,
): Promise<{ capas: Map<string, string>; bloques: Map<string, string> }> {
  const capas = new Map<string, string>();
  const bloques = new Map<string, string>();
  await Promise.all(censo.capas().map(async (n) => void capas.set(await huella(n), n)));
  for (const n of censo.bloques()) {
    const corto = n.split('$').pop() ?? n;
    if (corto !== n) {
      const hc = await huella(corto);
      if (!bloques.has(hc)) bloques.set(hc, corto);
    }
  }
  // Los nombres exactos mandan sobre los cortos.
  await Promise.all(censo.bloques().map(async (n) => void bloques.set(await huella(n), n)));
  return { capas, bloques };
}

/** DWG/DXF: los conceptos con piezas (los handles reales del dibujo). Una huella sin pareja en el dibujo se ignora (falla cerrada). */
export function pintablesDwg(
  a: AvancePublico,
  censo: CensoParaAvance,
  huellas: { capas: ReadonlyMap<string, string>; bloques: ReadonlyMap<string, string> },
): ConceptoParaPintar[] {
  const periodos = periodosParaCurva(a);
  const out: ConceptoParaPintar[] = [];
  for (const c of a.conceptos) {
    const d = c.piezasDwg;
    if (!d) continue;
    const handles = new Set<string>(d.handles ?? []);
    for (const capa of d.capas ?? []) {
      const nombre = huellas.capas.get(capa.h);
      if (!nombre) continue;
      for (const h of censo.handlesDeCapa(nombre, null)) {
        if (!capa.caja || dentroDeCaja(censo.centro(h), capa.caja)) handles.add(h);
      }
    }
    for (const b of d.bloques ?? []) {
      const nombre = huellas.bloques.get(b.h);
      if (!nombre) continue;
      for (const h of censo.handlesDeBloque(nombre)) handles.add(h);
    }
    const piezas = piezasDwgDeHandles(handles, censo);
    if (piezas.length === 0) continue;
    out.push({ nudos: nudosDeConcepto(a, c, periodos), piezas });
  }
  return out;
}
